import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Calendar, Clock, CheckCircle2, XCircle, AlertCircle, Save, Trash2, ListChecks, Users, Monitor, QrCode, BarChart3, Copy, RefreshCw, ExternalLink, ShieldCheck } from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";
import CheckinPolicyPanel from "../components/attendance/CheckinPolicyPanel";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DISCIPLINES = [
  ["kickboxing", "Kickboxing"],
  ["muay_thai", "Muay Thaï"],
  ["muay_thai_kickboxing", "Kickboxing / Muay Thaï"],
  ["jiu_jitsu_can_ryu", "Jiu-jitsu Can Ryu"],
  ["jiu_jitsu_bresilien", "Jiu-Jitsu Brésilien"],
  ["jiu_jitsu_kids", "Jiu-Jitsu Kids"],
  ["petits_samourais", "Petits Samouraïs (3-6 ans)"],
  ["mma", "MMA"],
  ["autre", "Autre"],
];

const STATUS_BTNS = [
  { value: "present", label: "Présent", icon: CheckCircle2, color: "bg-[#10b981]" },
  { value: "present_retard", label: "Retard", icon: Clock, color: "bg-[#f59e0b]" },
  { value: "absent_motive", label: "Abs. motivée", icon: AlertCircle, color: "bg-[#3b82f6]" },
  { value: "absent_non_motive", label: "Absent", icon: XCircle, color: "bg-[#D90429]" },
];

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Montreal" });
const nowTime = () => new Date().toLocaleTimeString("en-CA", { timeZone: "America/Montreal", hour: "2-digit", minute: "2-digit", hour12: false });

export default function AttendanceModule() {
  const [tab, setTab] = useState("saisie");
  // Saisie rapide
  const [sessionDraft, setSessionDraft] = useState({
    session_date: today(),
    start_time: nowTime(),
    discipline: "muay_thai_kickboxing",
    cours_type: "",
    coach_name: "",
    notes: "",
  });
  const [activeSession, setActiveSession] = useState(null);
  const [members, setMembers] = useState([]);
  const [recordsMap, setRecordsMap] = useState({}); // contract_id -> {presence_status, id}
  // Historique
  const [pastSessions, setPastSessions] = useState([]);
  const [openSession, setOpenSession] = useState(null);
  const [openRecords, setOpenRecords] = useState([]);
  // Dashboard
  const [dash, setDash] = useState(null);
  // Kiosque + check-ins + identités + rapports
  const [kioskCfg, setKioskCfg] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [hideDup, setHideDup] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [identities, setIdentities] = useState([]);
  const [identSearch, setIdentSearch] = useState("");
  const [qrMember, setQrMember] = useState(null);
  const [report, setReport] = useState(null);
  const [atRisk, setAtRisk] = useState(null);

  const loadKioskCfg = useCallback(() => {
    api.get("/attendance/kiosk-config").then(r => setKioskCfg(r.data)).catch(() => {});
  }, []);
  const loadCheckins = useCallback(() => {
    api.get("/attendance/checkins").then(r => { setCheckins(r.data.checkins || []); setPendingCount(r.data.pending || 0); }).catch(() => {});
  }, []);
  const loadIdentities = useCallback(() => {
    api.get("/attendance/identities").then(r => setIdentities(r.data.identities || [])).catch(() => {});
    api.post("/attendance/identities/send-email").then(r => setEmailInfo(r.data)).catch(() => {});
  }, []);
  const [emailInfo, setEmailInfo] = useState(null); // aperçu : targets, auto_enabled, last
  const sendPassEmailAll = async (onlyMissing) => {
    try {
      const { data } = await api.post(`/attendance/identities/send-email?only_missing=${onlyMissing}`);
      if (!data.resend_configured) { toast.error("Resend non configuré — impossible d'envoyer des courriels"); return; }
      if (!data.targets) { toast.info(onlyMissing ? "Tous les membres actifs avec courriel ont déjà reçu leur carte." : "Aucun membre actif avec courriel."); return; }
      const sample = (data.sample || []).map((s) => `• ${s.full_name} (${s.email})`).join("\n");
      const last = data.last?.at ? `\nDernier envoi : ${new Date(data.last.at).toLocaleString("fr-CA")} (${data.last.sent} envoyés)` : "";
      if (!window.confirm(`✉️ Envoyer le courriel « Ta carte de membre Impact » à ${data.targets} membre(s) actif(s) ${onlyMissing ? "qui ne l'ont reçue ni par courriel ni par SMS" : "(tous)"} ?\n\nAperçu :\n${sample}${data.targets > 8 ? `\n…+${data.targets - 8} autres` : ""}${last}\n\nCONFIRMER L'ENVOI RÉEL ?`)) return;
      const res = await api.post(`/attendance/identities/send-email?confirm=true&only_missing=${onlyMissing}`);
      toast.success(`${res.data.queued} courriel(s) en cours d'envoi en arrière-plan`);
      setTimeout(loadIdentities, 8000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi courriel"); }
  };
  const togglePassEmailAuto = async () => {
    const next = !(emailInfo?.auto_enabled !== false);
    try {
      await api.patch("/attendance/identities/email-auto", { enabled: next });
      setEmailInfo((p) => ({ ...(p || {}), auto_enabled: next }));
      toast.success(next ? "Rappel mensuel automatique activé (1er du mois)" : "Rappel mensuel automatique désactivé");
    } catch { toast.error("Erreur"); }
  };
  const loadReport = useCallback(() => {
    api.get("/attendance/reports").then(r => setReport(r.data)).catch(() => {});
  }, []);
  const loadAtRisk = useCallback(() => {
    api.get("/attendance/at-risk").then(r => setAtRisk(r.data)).catch(() => {});
  }, []);
  const relanceMember = async (r) => {
    const channels = [];
    if (window.confirm(`Envoyer une relance à ${r.full_name} ?\n\nCourriel + SMS (si disponibles) seront envoyés maintenant.`)) {
      channels.push("email", "sms");
    } else {
      return;
    }
    try {
      const res = await api.post(`/attendance/at-risk/${r.contract_id}/relance`, { channels, dry_run: false });
      const sent = res.data.sent || {};
      const okCanaux = Object.entries(sent).filter(([, v]) => v.ok).map(([k]) => k);
      const koCanaux = Object.entries(sent).filter(([, v]) => !v.ok).map(([k, v]) => `${k}: ${v.error || "échec"}`);
      if (okCanaux.length) toast.success(`Relance envoyée (${okCanaux.join(", ")})`);
      if (koCanaux.length) toast.error(koCanaux.join(" · "));
      loadAtRisk();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Relance impossible");
    }
  };

  const loadMembers = useCallback(() => {
    api.get("/student-contracts?statut=active").then(r => setMembers(r.data)).catch(() => {});
  }, []);

  const loadDashboard = useCallback(() => {
    api.get("/attendance/dashboard").then(r => setDash(r.data)).catch(() => {});
  }, []);

  const loadHistory = useCallback(() => {
    api.get("/attendance/sessions").then(r => setPastSessions(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadMembers(); loadDashboard(); loadCheckins(); }, [loadMembers, loadDashboard, loadCheckins]);
  useEffect(() => { if (tab === "historique") loadHistory(); }, [tab, loadHistory]);
  useEffect(() => { if (tab === "kiosque") loadKioskCfg(); }, [tab, loadKioskCfg]);
  useEffect(() => { if (tab === "valider") loadCheckins(); }, [tab, loadCheckins]);
  useEffect(() => { if (tab === "cartes") { loadIdentities(); } }, [tab, loadIdentities]);
  useEffect(() => { if (tab === "rapports") loadReport(); }, [tab, loadReport]);
  useEffect(() => { if (tab === "a-risque") loadAtRisk(); }, [tab, loadAtRisk]);

  // Filtre membres par discipline (incluant muay_thai_kickboxing combiné)
  const membersFiltered = members.filter(m => {
    if (!activeSession && !sessionDraft.discipline) return true;
    const d = activeSession?.discipline || sessionDraft.discipline;
    if (d === "muay_thai_kickboxing") return ["muay_thai_kickboxing", "muay_thai", "kickboxing"].includes(m.discipline);
    if (d === "muay_thai" || d === "kickboxing") return ["muay_thai_kickboxing", d].includes(m.discipline);
    return m.discipline === d;
  });

  const createSession = async () => {
    try {
      const r = await api.post("/attendance/sessions", sessionDraft);
      setActiveSession(r.data);
      setRecordsMap({});
      toast.success("Session créée — marque les présences");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const markPresence = async (contractId, status) => {
    if (!activeSession) {
      toast.error("Crée d'abord la session ci-dessus");
      return;
    }
    try {
      const r = await api.post("/attendance/records", {
        session_id: activeSession.id,
        contract_id: contractId,
        presence_status: status,
      });
      setRecordsMap(prev => ({ ...prev, [contractId]: r.data }));
    } catch {
      toast.error("Erreur enregistrement");
    }
  };

  const markAllPresent = async () => {
    if (!activeSession) {
      toast.error("Crée d'abord la session");
      return;
    }
    let count = 0;
    for (const m of membersFiltered) {
      if (recordsMap[m.id]) continue;
      try {
        const r = await api.post("/attendance/records", {
          session_id: activeSession.id, contract_id: m.id, presence_status: "present",
        });
        setRecordsMap(prev => ({ ...prev, [m.id]: r.data }));
        count++;
      } catch { /* skip */ }
    }
    toast.success(`${count} présence(s) marquée(s)`);
    loadDashboard();
  };

  const closeSession = () => {
    setActiveSession(null);
    setRecordsMap({});
    setSessionDraft({ ...sessionDraft, session_date: today(), start_time: nowTime() });
    loadDashboard();
  };

  const openHistoricalSession = async (s) => {
    setOpenSession(s);
    try {
      const r = await api.get(`/attendance/sessions/${s.id}/records`);
      setOpenRecords(r.data);
    } catch { setOpenRecords([]); }
  };

  const deleteHistoricalSession = async (s) => {
    if (!window.confirm(`Supprimer la session du ${s.session_date} (${s.discipline}) ?`)) return;
    try {
      await api.delete(`/attendance/sessions/${s.id}`);
      toast.success("Session supprimée");
      setOpenSession(null);
      loadHistory();
    } catch { toast.error("Erreur"); }
  };

  // ── Kiosque ──
  const saveKioskCfg = async () => {
    try {
      const r = await api.put("/attendance/kiosk-config", {
        kiosk_code: kioskCfg.kiosk_code,
        require_geofence: kioskCfg.require_geofence,
        location: kioskCfg.location,
      });
      setKioskCfg(r.data);
      toast.success("Configuration du kiosque enregistrée");
    } catch { toast.error("Erreur"); }
  };

  // ── Validation coach ──
  const validateCheckin = async (id, status) => {
    try {
      await api.post(`/attendance/checkins/${id}/validate`, status ? { presence_status: status } : {});
      loadCheckins(); loadDashboard();
    } catch { toast.error("Erreur"); }
  };
  const rejectCheckin = async (id) => {
    try {
      await api.post(`/attendance/checkins/${id}/reject`);
      loadCheckins(); loadDashboard();
    } catch { toast.error("Erreur"); }
  };
  const validateAll = async () => {
    try {
      const r = await api.post("/attendance/checkins/validate-all");
      toast.success(`${r.data.validated} présence(s) validée(s)`);
      loadCheckins(); loadDashboard();
    } catch { toast.error("Erreur"); }
  };

  // ── Identités / cartes ──
  const syncIdentities = async () => {
    try {
      const r = await api.post("/attendance/identities/sync");
      toast.success(`${r.data.created} carte(s) générée(s) · ${r.data.total} au total`);
      loadIdentities();
    } catch { toast.error("Erreur"); }
  };
  const regenIdentity = async (cid) => {
    try {
      await api.post(`/attendance/identities/${cid}/regenerate`);
      toast.success("QR + PIN régénérés");
      loadIdentities();
    } catch { toast.error("Erreur"); }
  };
  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copié"); };
  const sendPassSmsAll = async (onlyMissing) => {
    try {
      const { data } = await api.post(`/attendance/identities/send-sms?only_missing=${onlyMissing}`);
      if (!data.twilio_configured) { toast.error("Twilio non configuré — impossible d'envoyer des SMS"); return; }
      if (!data.targets) { toast.info(onlyMissing ? "Tous les membres avec téléphone ont déjà reçu leur carte." : "Aucun membre actif avec téléphone."); return; }
      const sample = (data.sample || []).map((s) => `• ${s.full_name} (${s.phone})`).join("\n");
      const last = data.last?.at ? `\nDernier envoi : ${new Date(data.last.at).toLocaleString("fr-CA")} (${data.last.sent} envoyés)` : "";
      if (!window.confirm(`📱 Envoyer le lien de la carte QR par SMS à ${data.targets} membre(s) actif(s) ${onlyMissing ? "qui ne l'ont pas encore reçu" : "(tous, même déjà envoyé)"} ?\n\nMessage :\n${data.message_preview}\n\nAperçu :\n${sample}${data.targets > 8 ? `\n…+${data.targets - 8} autres` : ""}${last}\n\nCONFIRMER L'ENVOI RÉEL ?`)) return;
      const res = await api.post(`/attendance/identities/send-sms?confirm=true&only_missing=${onlyMissing}`);
      toast.success(`${res.data.queued} SMS en cours d'envoi en arrière-plan`);
      setTimeout(loadIdentities, 8000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi SMS"); }
  };
  const sendPassSmsOne = async (i) => {
    if (!window.confirm(`Envoyer la carte QR par SMS à ${i.full_name} (${i.phone}) ?`)) return;
    try {
      await api.post(`/attendance/identities/${i.contract_id}/send-sms`);
      toast.success(`SMS envoyé à ${i.full_name}`);
      loadIdentities();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi SMS"); }
  };

  const CHECKIN_LABEL = {
    present: ["Présent", "bg-[#10b981]"], present_retard: ["Retard", "bg-[#f59e0b]"],
    essai: ["Essai", "bg-cyan-600"], doublon: ["Doublon", "bg-blue-600"],
    refuse: ["Refusé", "bg-[#D90429]"], absent_motive: ["Abs. motivée", "bg-[#3b82f6]"],
    absent_non_motive: ["Absent", "bg-[#D90429]"],
  };
  const [identFilter, setIdentFilter] = useState("all"); // all | no_photo | no_phone
  const noPhotoCount = identities.filter((i) => !i.photo_url).length;
  const identsFiltered = identities.filter(i => (!identSearch || (i.full_name || "").toLowerCase().includes(identSearch.toLowerCase()))
    && (identFilter === "all" || (identFilter === "no_photo" && !i.photo_url) || (identFilter === "no_phone" && !i.phone)));

  return (
    <div data-testid="attendance-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader title={<>Présences</>} subtitle={<>Phase A · suivi quotidien des cours · décrocheurs détectés automatiquement.</>} />
        <CheckinPolicyPanel />
        <div className="mt-4 flex flex-wrap items-center gap-4 border border-[#C9A961]/40 bg-[#C9A961]/5 p-4" data-testid="tablet-qr-card">
          <div className="bg-white p-2"><QRCodeCanvas value={`${window.location.origin}/tablette-coach`} size={96} data-testid="tablet-qr" /></div>
          <div className="text-sm">
            <div className="font-bold">📱 Tablette coach — présences & séances de groupe</div>
            <div className="text-muted-foreground text-xs mt-1">Chaque instructeur scanne ce code (ou ouvre <span className="select-all font-mono">{window.location.origin}/tablette-coach</span>) sur son téléphone ou la tablette du dojo. Code du dojo : <b>DOJO2026</b> (identique au kiosque).</div>
            <div className="flex gap-2 mt-2">
              <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/tablette-coach`); toast.success("Lien copié"); }} className="px-3 py-1.5 text-xs border border-white/20 hover:bg-white hover:text-black" data-testid="tablet-copy-link">Copier le lien</button>
              <a href="/tablette-coach" target="_blank" rel="noreferrer" className="px-3 py-1.5 text-xs bg-[#C9A961] text-black font-bold" data-testid="tablet-open-link">Ouvrir la tablette</a>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs DASHBOARD */}
      {dash && (
        <div className="hard-border-b grid grid-cols-2 md:grid-cols-4 gap-0">
          <div className="hard-border-r px-6 py-5">
            <div className="ticker text-muted-foreground mb-1">SESSIONS / SEMAINE</div>
            <div className="text-2xl font-black">{dash.sessions_semaine}</div>
          </div>
          <div className="hard-border-r px-6 py-5">
            <div className="ticker text-muted-foreground mb-1">PRÉSENCES / SEMAINE</div>
            <div className="text-2xl font-black text-impact-gold">{dash.presences_semaine}</div>
          </div>
          <div className="hard-border-r px-6 py-5">
            <div className="ticker text-muted-foreground mb-1">VUS AUJOURD'HUI</div>
            <div className="text-2xl font-black text-[#10b981]">{dash.eleves_vus_aujourdhui}</div>
          </div>
          <div className="px-6 py-5">
            <div className="ticker text-muted-foreground mb-1">⚠️ DÉCROCHEURS (14j+)</div>
            <div className="text-2xl font-black text-[#D90429]">{dash.decrocheurs_count}</div>
          </div>
        </div>
      )}

      {/* TABS */}
      <div className="hard-border-b px-8 lg:px-12 py-3 flex items-center gap-3 bg-secondary/30">
        <button data-testid="tab-saisie" onClick={() => setTab("saisie")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "saisie" ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
          <ListChecks size={14}/> Saisie rapide
        </button>
        <button data-testid="tab-historique" onClick={() => setTab("historique")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "historique" ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
          <Calendar size={14}/> Historique
        </button>
        <button data-testid="tab-decrocheurs" onClick={() => setTab("decrocheurs")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "decrocheurs" ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
          <Users size={14}/> Décrocheurs ({dash?.decrocheurs_count ?? 0})
        </button>
        <button data-testid="tab-a-risque" onClick={() => setTab("a-risque")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "a-risque" ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
          <Users size={14}/> À risque{atRisk ? ` (${atRisk.at_risk.length})` : ""}
        </button>
        <button data-testid="tab-valider" onClick={() => setTab("valider")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "valider" ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
          <ShieldCheck size={14}/> À valider {pendingCount > 0 ? `(${pendingCount})` : ""}
        </button>
        <button data-testid="tab-kiosque" onClick={() => setTab("kiosque")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "kiosque" ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
          <Monitor size={14}/> Kiosque
        </button>
        <button data-testid="tab-cartes" onClick={() => setTab("cartes")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "cartes" ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
          <QrCode size={14}/> Cartes membres
        </button>
        <button data-testid="tab-rapports" onClick={() => setTab("rapports")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "rapports" ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
          <BarChart3 size={14}/> Rapports
        </button>
      </div>

      {tab === "saisie" && (
        <div className="px-8 lg:px-12 py-6" data-testid="view-saisie">
          {!activeSession ? (
            <div className="hard-border p-4 bg-secondary/10 max-w-3xl">
              <h3 className="font-display font-bold uppercase mb-3">Nouveau cours</h3>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <input data-testid="s-date" type="date" value={sessionDraft.session_date} onChange={(e) => setSessionDraft({...sessionDraft, session_date: e.target.value})} className="hard-border px-3 py-2"/>
                <input data-testid="s-time" type="time" value={sessionDraft.start_time} onChange={(e) => setSessionDraft({...sessionDraft, start_time: e.target.value})} className="hard-border px-3 py-2"/>
                <select data-testid="s-discipline" value={sessionDraft.discipline} onChange={(e) => setSessionDraft({...sessionDraft, discipline: e.target.value})} className="hard-border px-3 py-2 bg-white">
                  {DISCIPLINES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input data-testid="s-type" placeholder="Type cours (Cardio, Sparring, etc.)" value={sessionDraft.cours_type} onChange={(e) => setSessionDraft({...sessionDraft, cours_type: e.target.value})} className="hard-border px-3 py-2"/>
                <input data-testid="s-coach" placeholder="Coach" value={sessionDraft.coach_name} onChange={(e) => setSessionDraft({...sessionDraft, coach_name: e.target.value})} className="hard-border px-3 py-2 col-span-2"/>
              </div>
              <button data-testid="s-create" onClick={createSession} className="bg-impact-red hover:bg-foreground text-white px-4 py-2 ticker w-full transition-colors flex items-center gap-2 justify-center">
                <Save size={14}/> Créer la session et marquer les présences
              </button>
            </div>
          ) : (
            <div>
              <div className="hard-border p-3 mb-4 flex items-center justify-between bg-impact-gold/10">
                <div>
                  <div className="ticker text-muted-foreground">SESSION ACTIVE</div>
                  <div className="font-display font-bold text-lg">{activeSession.session_date} · {activeSession.start_time} · {DISCIPLINES.find(([v]) => v === activeSession.discipline)?.[1]}</div>
                  <div className="text-xs text-muted-foreground">{activeSession.cours_type} {activeSession.coach_name ? `· coach ${activeSession.coach_name}`: ""}</div>
                </div>
                <div className="flex gap-2">
                  <button data-testid="s-all-present" onClick={markAllPresent} className="bg-[#10b981] hover:bg-[#0ea372] text-white px-3 py-2 ticker">Tout présent</button>
                  <button data-testid="s-close" onClick={closeSession} className="hard-border px-3 py-2 ticker">Fermer</button>
                </div>
              </div>
              <div className="space-y-1">
                {membersFiltered.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">Aucun élève actif pour cette discipline.</div>}
                {membersFiltered.map((m) => {
                  const rec = recordsMap[m.id];
                  return (
                    <div key={m.id} className="hard-border px-3 py-2 flex items-center justify-between hover:bg-secondary/20" data-testid={`row-att-${m.id}`}>
                      <div className="text-sm">
                        <span className="font-medium">{m.full_name}</span>
                        <span className="text-xs text-muted-foreground ml-2">{m.niveau_ceinture || "—"}</span>
                      </div>
                      <div className="flex gap-1">
                        {STATUS_BTNS.map((b) => {
                          const Icon = b.icon;
                          const active = rec?.presence_status === b.value;
                          return (
                            <button
                              key={b.value}
                              data-testid={`att-${b.value}-${m.id}`}
                              onClick={() => markPresence(m.id, b.value)}
                              className={`px-2 py-1 ticker text-xs flex items-center gap-1 ${active ? `${b.color} text-white` : "hover:bg-secondary"}`}
                              title={b.label}
                            >
                              <Icon size={12}/>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "historique" && (
        <div className="px-8 lg:px-12 py-6" data-testid="view-historique">
          {pastSessions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">Aucune session enregistrée.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {pastSessions.map((s) => (
                <div key={s.id} className="hard-border p-3 hover:bg-secondary/20" data-testid={`past-${s.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display font-bold">{s.session_date} · {s.start_time || "—"}</div>
                      <div className="text-sm text-muted-foreground">{DISCIPLINES.find(([v]) => v === s.discipline)?.[1]} · {s.cours_type || "—"}</div>
                      <div className="text-xs text-muted-foreground">{s.coach_name ? `Coach: ${s.coach_name}` : ""}</div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openHistoricalSession(s)} className="ticker px-2 py-1 hard-border text-xs">Voir</button>
                      <button onClick={() => deleteHistoricalSession(s)} className="p-1.5 hover:bg-[#D90429]/10 hover:text-[#D90429]"><Trash2 size={12}/></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {openSession && (
            <div className="fixed inset-0 z-50 flex" data-testid="hist-modal">
              <div className="absolute inset-0 bg-black/60" onClick={() => setOpenSession(null)}/>
              <div className="ml-auto w-full max-w-xl bg-white h-full overflow-y-auto relative">
                <div className="hard-border-b p-4 sticky top-0 bg-white flex justify-between">
                  <h3 className="font-display font-bold">{openSession.session_date} · {openSession.discipline}</h3>
                  <button onClick={() => setOpenSession(null)}>✕</button>
                </div>
                <div className="p-4 space-y-1">
                  {openRecords.length === 0 && <p className="text-muted-foreground text-sm">Aucune présence enregistrée.</p>}
                  {openRecords.map((r) => {
                    const m = members.find(x => x.id === r.contract_id);
                    const btn = STATUS_BTNS.find(b => b.value === r.presence_status);
                    return (
                      <div key={r.id} className="hard-border px-3 py-2 flex justify-between text-sm">
                        <span>{m?.full_name || r.contract_id}</span>
                        <span className={`ticker px-2 py-0.5 ${btn?.color} text-white`}>{btn?.label || r.presence_status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "decrocheurs" && (
        <div className="px-8 lg:px-12 py-6" data-testid="view-decrocheurs">
          {!dash?.decrocheurs?.length ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 size={48} className="mx-auto mb-3 text-[#10b981] opacity-50"/>
              <p>Aucun décrocheur détecté. Tous tes élèves actifs sont venus dans les 14 derniers jours.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-4">⚠️ Élèves actifs <strong>pas vus depuis 14 jours ou plus</strong>. À contacter avant qu'ils ne décrochent vraiment.</p>
              {dash.decrocheurs.map((d) => (
                <div key={d.contract_id} className="hard-border px-3 py-2 flex justify-between items-center" data-testid={`dec-${d.contract_id}`}>
                  <div>
                    <div className="font-medium">{d.full_name}</div>
                    <div className="text-xs text-muted-foreground">Dernière présence : {d.derniere_presence || "jamais vu"}</div>
                  </div>
                  <span className="ticker bg-[#D90429]/15 text-[#D90429] px-2 py-1 text-xs">À RAPPELER</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── À RISQUE (présences ↔ contrats actifs ↔ carnet) ── */}
      {tab === "a-risque" && (
        <div className="px-8 lg:px-12 py-6" data-testid="view-a-risque">
          {!atRisk ? (
            <div className="text-center py-12 text-muted-foreground">Chargement…</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="hard-border px-3 py-2 text-center" data-testid="risk-count-assidu">
                  <div className="text-2xl font-black text-[#10b981]">{atRisk.counts.assidu}</div>
                  <div className="ticker text-xs text-muted-foreground">Assidus</div>
                </div>
                <div className="hard-border px-3 py-2 text-center" data-testid="risk-count-a-risque">
                  <div className="text-2xl font-black text-[#f59e0b]">{atRisk.counts.a_risque}</div>
                  <div className="ticker text-xs text-muted-foreground">À risque</div>
                </div>
                <div className="hard-border px-3 py-2 text-center" data-testid="risk-count-decrocheur">
                  <div className="text-2xl font-black text-[#D90429]">{atRisk.counts.decrocheur}</div>
                  <div className="ticker text-xs text-muted-foreground">Décrocheurs</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Croise la <strong>fréquence réelle</strong> (28 derniers jours) avec le <strong>plan du contrat</strong> et la dernière présence.
                Seuls les contrats <strong>actifs</strong> sont suivis. La ceinture provient du carnet de progression.
              </p>
              {atRisk.at_risk.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 size={48} className="mx-auto mb-3 text-[#10b981] opacity-50"/>
                  <p>Aucun membre à risque. Tout le monde est assidu.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {atRisk.at_risk.map((r) => (
                    <div key={r.contract_id} className="hard-border px-3 py-2 flex justify-between items-center gap-3" data-testid={`risk-${r.contract_id}`}>
                      <div className="min-w-0">
                        <div className="font-medium flex items-center gap-2">
                          {r.full_name}
                          {r.ceinture_actuelle && <span className="text-[10px] uppercase tracking-wide bg-secondary px-1.5 py-0.5">{r.ceinture_actuelle}</span>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {r.reasons.join(" · ")} — {r.frequence_reelle_hebdo}/sem (plan {r.sessions_per_week}/sem)
                          {r.derniere_relance && <span className="text-[#10b981]"> · relancé le {r.derniere_relance.slice(0, 10)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button data-testid={`relance-${r.contract_id}`} onClick={() => relanceMember(r)}
                          className="ticker px-2 py-1 text-xs bg-[#D90429] text-white hover:bg-[#b8031f] transition-colors">
                          ✉️ Relancer
                        </button>
                        <span className={`ticker px-2 py-1 text-xs ${r.risk_level === "decrocheur" ? "bg-[#D90429]/15 text-[#D90429]" : "bg-[#f59e0b]/15 text-[#b45309]"}`}>
                          {r.risk_level === "decrocheur" ? "DÉCROCHEUR" : "À RISQUE"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {tab === "valider" && (
        <div className="px-8 lg:px-12 py-6" data-testid="view-valider">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <p className="text-sm text-muted-foreground">Check-ins du jour faits au kiosque. Valide, corrige ou rejette.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer" data-testid="hide-dup-toggle">
                <input type="checkbox" checked={hideDup} onChange={(e) => setHideDup(e.target.checked)} className="accent-[#D90429]" />
                Masquer les doublons
              </label>
              {checkins.some(c => c.presence_status === "doublon" || c.status === "duplicate") && (
                <button data-testid="reject-dup-btn"
                  onClick={async () => {
                    const n = checkins.filter(c => (c.presence_status === "doublon" || c.status === "duplicate") && c.status !== "rejected" && c.status !== "validated").length;
                    if (!n) { toast.info("Aucun doublon à rejeter."); return; }
                    if (!window.confirm(`Rejeter ${n} check-in(s) en doublon du jour ?`)) return;
                    try {
                      const { data } = await api.post("/attendance/checkins/reject-duplicates");
                      toast.success(`${data.rejected} doublon(s) rejeté(s)`);
                      loadCheckins();
                    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}
                  className="bg-amber-600 hover:bg-amber-500 text-black px-3 py-2 ticker text-sm font-semibold">
                  🧹 Rejeter les doublons
                </button>
              )}
              {checkins.some(c => c.status === "pending") && (
                <button data-testid="validate-all-btn" onClick={validateAll} className="bg-[#10b981] hover:bg-[#0ea372] text-white px-3 py-2 ticker">Tout valider</button>
              )}
            </div>
          </div>
          {(() => { const visible = hideDup ? checkins.filter(c => c.presence_status !== "doublon" && c.status !== "duplicate") : checkins; return visible.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">{hideDup && checkins.length ? "Aucun check-in hors doublons." : "Aucun check-in enregistré aujourd'hui."}</div>
          ) : (
            <div className="space-y-2">
              {visible.map((c) => {
                const [lbl, color] = CHECKIN_LABEL[c.presence_status] || [c.presence_status, "bg-secondary"];
                const isPending = c.status === "pending";
                return (
                  <div key={c.id} className="hard-border px-3 py-2 flex items-center justify-between" data-testid={`checkin-${c.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-impact-gold/20 flex items-center justify-center font-bold text-sm">{(c.member_name || "?").charAt(0)}</div>
                      <div>
                        <div className="font-medium text-sm">{c.member_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {c.matched_course || "—"} · {c.method?.replace("_", " ")} · {(c.checkin_at || "").slice(11, 16)}
                          {c.location_verified === true && <span className="text-[#10b981] ml-1">· 📍 GPS ok</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`ticker px-2 py-0.5 text-xs text-white ${color}`}>{lbl}</span>
                      {isPending ? (
                        <>
                          <button data-testid={`checkin-validate-${c.id}`} onClick={() => validateCheckin(c.id)} className="bg-[#10b981] text-white px-2 py-1 ticker text-xs flex items-center gap-1"><CheckCircle2 size={12}/> Valider</button>
                          <button data-testid={`checkin-late-${c.id}`} onClick={() => validateCheckin(c.id, "present_retard")} className="bg-[#f59e0b] text-white px-2 py-1 ticker text-xs" title="Marquer retard"><Clock size={12}/></button>
                          <button data-testid={`checkin-reject-${c.id}`} onClick={() => rejectCheckin(c.id)} className="hard-border px-2 py-1 ticker text-xs hover:bg-[#D90429] hover:text-white"><XCircle size={12}/></button>
                        </>
                      ) : (
                        <span className="ticker text-xs text-muted-foreground">{c.status === "validated" ? "✓ validé" : c.status === "rejected" ? "rejeté" : c.status}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ); })()}
        </div>
      )}

      {/* ── KIOSQUE (config) ── */}
      {tab === "kiosque" && kioskCfg && (
        <div className="px-8 lg:px-12 py-6 max-w-2xl" data-testid="view-kiosque">
          <div className="hard-border p-4 mb-4 bg-secondary/10">
            <h3 className="font-display font-bold uppercase mb-3">Mode kiosque tablette</h3>
            <p className="text-sm text-muted-foreground mb-4">Configure une tablette à l'entrée. Les élèves scannent leur QR, entrent leur PIN ou cherchent leur nom. Chaque présence est <strong>provisoire</strong> jusqu'à validation dans l'onglet « À valider ».</p>
            <label className="ticker text-muted-foreground">Code du kiosque (secret)</label>
            <input data-testid="kiosk-code-field" value={kioskCfg.kiosk_code || ""} onChange={(e) => setKioskCfg({ ...kioskCfg, kiosk_code: e.target.value })} placeholder="Ex: DOJO2026" className="hard-border px-3 py-2 w-full mb-3 mt-1"/>
            <label className="ticker text-muted-foreground">Nom affiché</label>
            <input value={kioskCfg.location?.name || ""} onChange={(e) => setKioskCfg({ ...kioskCfg, location: { ...kioskCfg.location, name: e.target.value } })} className="hard-border px-3 py-2 w-full mb-3 mt-1"/>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <label className="ticker text-muted-foreground text-xs">Latitude (géofence)</label>
                <input type="number" step="any" value={kioskCfg.location?.latitude ?? ""} onChange={(e) => setKioskCfg({ ...kioskCfg, location: { ...kioskCfg.location, latitude: e.target.value === "" ? null : parseFloat(e.target.value) } })} className="hard-border px-2 py-2 w-full"/>
              </div>
              <div>
                <label className="ticker text-muted-foreground text-xs">Longitude</label>
                <input type="number" step="any" value={kioskCfg.location?.longitude ?? ""} onChange={(e) => setKioskCfg({ ...kioskCfg, location: { ...kioskCfg.location, longitude: e.target.value === "" ? null : parseFloat(e.target.value) } })} className="hard-border px-2 py-2 w-full"/>
              </div>
              <div>
                <label className="ticker text-muted-foreground text-xs">Rayon (m)</label>
                <input type="number" value={kioskCfg.location?.geofence_radius_meters ?? 150} onChange={(e) => setKioskCfg({ ...kioskCfg, location: { ...kioskCfg.location, geofence_radius_meters: parseInt(e.target.value) || 150 } })} className="hard-border px-2 py-2 w-full"/>
              </div>
            </div>
            <button
              type="button"
              data-testid="kiosk-capture-gps"
              onClick={() => {
                if (!navigator.geolocation) { toast.error("Géolocalisation non disponible"); return; }
                toast.info("Capture de la position… (sois physiquement au dojo)");
                navigator.geolocation.getCurrentPosition(
                  (p) => { setKioskCfg((c) => ({ ...c, location: { ...c.location, latitude: +p.coords.latitude.toFixed(6), longitude: +p.coords.longitude.toFixed(6) } })); toast.success("Position capturée — clique Enregistrer"); },
                  () => toast.error("Impossible d'obtenir la position"),
                  { enableHighAccuracy: true, timeout: 8000 }
                );
              }}
              className="hard-border px-3 py-2 ticker w-full mb-3 hover:bg-secondary transition-colors flex items-center justify-center gap-2"
            >
              📍 Capturer la position GPS du dojo (depuis cet appareil sur place)
            </button>
            <label className="flex items-center gap-2 mb-4 text-sm">
              <input type="checkbox" data-testid="kiosk-geofence-toggle" checked={!!kioskCfg.require_geofence} onChange={(e) => setKioskCfg({ ...kioskCfg, require_geofence: e.target.checked })}/>
              Exiger la présence GPS dans le rayon (Phase 3 — facultatif)
            </label>
            <button data-testid="kiosk-save-btn" onClick={saveKioskCfg} className="bg-impact-red hover:bg-foreground text-white px-4 py-2 ticker w-full transition-colors flex items-center gap-2 justify-center">
              <Save size={14}/> Enregistrer la configuration
            </button>
          </div>
          <div className="hard-border p-4 flex items-center justify-between bg-impact-gold/10">
            <div>
              <div className="ticker text-muted-foreground">Ouvrir le mode kiosque</div>
              <div className="text-xs text-muted-foreground">Ouvre /kiosque sur la tablette puis entre le code.</div>
            </div>
            <a data-testid="kiosk-open-link" href={`${BACKEND}/kiosque`} target="_blank" rel="noreferrer" className="bg-foreground text-white px-3 py-2 ticker flex items-center gap-2"><ExternalLink size={14}/> Ouvrir</a>
          </div>
        </div>
      )}

      {/* ── CARTES MEMBRES (QR + PIN) ── */}
      {tab === "cartes" && (
        <div className="px-8 lg:px-12 py-6" data-testid="view-cartes">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <input data-testid="ident-search" value={identSearch} onChange={(e) => setIdentSearch(e.target.value)} placeholder="Chercher un membre…" className="hard-border px-3 py-2 flex-1 max-w-sm"/>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="ident-sms-all-btn" onClick={() => sendPassSmsAll(true)} className="bg-emerald-700 hover:bg-emerald-600 text-white px-3 py-2 ticker flex items-center gap-2" title="SMS aux membres actifs qui n'ont pas encore reçu leur carte">📱 Envoyer les cartes par SMS</button>
              <button data-testid="ident-sms-resend-btn" onClick={() => sendPassSmsAll(false)} className="hard-border px-3 py-2 ticker text-xs" title="Renvoyer à tous les membres actifs avec téléphone">↻ Renvoyer à tous</button>
              <button data-testid="ident-sync-btn" onClick={syncIdentities} className="bg-impact-red hover:bg-foreground text-white px-3 py-2 ticker flex items-center gap-2"><RefreshCw size={14}/> Générer les cartes manquantes</button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2 mb-3" data-testid="ident-sms-stats">
            {identities.filter((i) => i.pass_sms_sent_at).length} / {identities.length} carte(s) envoyée(s) par SMS · {identities.filter((i) => i.pass_email_sent_at).length} par courriel · {identities.filter((i) => !i.phone).length} sans téléphone
          </p>
          {noPhotoCount > 0 && (
            <div className="border border-amber-500/50 bg-amber-500/10 p-3 mb-3 flex items-center justify-between gap-3 flex-wrap" data-testid="ident-no-photo-alert">
              <div className="text-xs">
                <div className="font-medium">📷 {noPhotoCount} membre(s) actif(s) sans photo sur leur carte</div>
                <div className="text-muted-foreground">Demande-leur à la prochaine visite : ils touchent leur avatar sur la carte (ou l'accueil prend la photo depuis leur téléphone).</div>
              </div>
              <div className="flex gap-1">
                {[["all", "Tous"], ["no_photo", `📷 Sans photo (${noPhotoCount})`], ["no_phone", `📱 Sans téléphone (${identities.filter((i) => !i.phone).length})`]].map(([k, l]) => (
                  <button key={k} onClick={() => setIdentFilter(k)} data-testid={`ident-filter-${k}`}
                    className={`ticker text-xs px-2.5 py-1.5 hard-border ${identFilter === k ? "bg-foreground text-background" : ""}`}>{l}</button>
                ))}
              </div>
            </div>
          )}
          <div className="hard-border p-3 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="ident-email-panel">
            <div className="text-xs">
              <div className="font-medium">✉️ Carte par courriel</div>
              <div className="text-muted-foreground">
                {emailInfo ? `${emailInfo.targets} membre(s) actif(s) n'ont encore reçu leur carte ni par courriel ni par SMS` : "…"}
                {emailInfo?.last?.at ? ` · dernier envoi ${new Date(emailInfo.last.at).toLocaleDateString("fr-CA")} (${emailInfo.last.sent} envoyés${emailInfo.last.source === "monthly" ? ", auto" : ""})` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="ident-email-auto-toggle" onClick={togglePassEmailAuto}
                className={`ticker text-xs px-3 py-2 hard-border ${emailInfo && emailInfo.auto_enabled !== false ? "bg-emerald-600 text-white" : ""}`}
                title="Le 1er de chaque mois, courriel automatique aux nouveaux membres actifs qui n'ont pas encore reçu leur carte">
                {!emailInfo ? "Rappel mensuel auto : …" : emailInfo.auto_enabled !== false ? "● Rappel mensuel auto : ON" : "○ Rappel mensuel auto : OFF"}
              </button>
              <button data-testid="ident-email-all-btn" onClick={() => sendPassEmailAll(true)} className="bg-impact-red hover:bg-foreground text-white px-3 py-2 ticker text-xs">✉️ Envoyer maintenant aux manquants</button>
              <button data-testid="ident-email-resend-btn" onClick={() => sendPassEmailAll(false)} className="hard-border px-3 py-2 ticker text-xs">↻ Renvoyer à tous</button>
            </div>
          </div>
          {identities.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">Aucune carte. Clique « Générer les cartes manquantes ».</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {identsFiltered.map((i) => (
                <div key={i.contract_id} className="hard-border p-3" data-testid={`ident-${i.contract_id}`}>
                  <div className="font-medium text-sm flex items-center gap-2">
                    {i.photo_url ? <img src={i.photo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${i.photo_url}` : i.photo_url} alt="" className="w-7 h-7 rounded-full object-cover border border-zinc-300" /> : <span className="w-7 h-7 rounded-full bg-amber-500/20 border border-amber-500/50 flex items-center justify-center text-[10px]" title="Sans photo">📷</span>}
                    {i.full_name}
                  </div>
                  <div className="text-xs text-muted-foreground capitalize mb-2">{(i.discipline || "").replace("_", " ")}{i.pass_email_sent_at ? <span className="ml-2 text-emerald-600 normal-case" title={`Courriel envoyé le ${new Date(i.pass_email_sent_at).toLocaleDateString("fr-CA")}`}>✉️ envoyé</span> : null}</div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs">
                      <div className="ticker text-muted-foreground">PIN</div>
                      <div className="font-mono font-bold text-lg tracking-widest">{i.pin_code}</div>
                    </div>
                    <button onClick={() => setQrMember(i)} data-testid={`ident-qr-${i.contract_id}`} className="bg-white border border-zinc-200 p-1 rounded">
                      <QRCodeCanvas value={i.qr_token} size={56} level="M"/>
                    </button>
                  </div>
                  <div className="flex gap-1 mt-3">
                    <button onClick={() => copyText(i.pass_url)} className="ticker text-xs hard-border px-2 py-1 flex items-center gap-1 flex-1 justify-center"><Copy size={11}/> Lien carte</button>
                    <button onClick={() => sendPassSmsOne(i)} disabled={!i.phone} data-testid={`ident-sms-${i.contract_id}`}
                      className={`ticker text-xs hard-border px-2 py-1 flex items-center gap-1 disabled:opacity-40 ${i.pass_sms_sent_at ? "text-emerald-600" : ""}`}
                      title={!i.phone ? "Aucun téléphone" : i.pass_sms_sent_at ? `SMS envoyé le ${new Date(i.pass_sms_sent_at).toLocaleDateString("fr-CA")} — renvoyer` : `SMS à ${i.phone}`}>
                      📱 {i.pass_sms_sent_at ? "Envoyé ✓" : "SMS"}
                    </button>
                    <button onClick={() => regenIdentity(i.contract_id)} className="ticker text-xs hard-border px-2 py-1 flex items-center gap-1" title="Régénérer QR + PIN"><RefreshCw size={11}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {qrMember && (
            <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="qr-modal" onClick={() => setQrMember(null)}>
              <div className="absolute inset-0 bg-black/70"/>
              <div className="relative bg-white p-6 text-center max-w-xs" onClick={(e) => e.stopPropagation()}>
                <div className="font-display font-bold mb-1">{qrMember.full_name}</div>
                <div className="text-xs text-muted-foreground mb-4">PIN {qrMember.pin_code}</div>
                <div className="flex justify-center mb-4"><QRCodeCanvas value={qrMember.qr_token} size={240} level="M" includeMargin/></div>
                <button onClick={() => setQrMember(null)} className="bg-foreground text-white px-4 py-2 ticker w-full">Fermer</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── RAPPORTS ── */}
      {tab === "rapports" && (
        <div className="px-8 lg:px-12 py-6" data-testid="view-rapports">
          {!report ? (
            <div className="text-center py-12 text-muted-foreground">Chargement du rapport…</div>
          ) : (
            <div className="space-y-6">
              <p className="text-sm text-muted-foreground">Période : {report.date_from} → {report.date_to} · {report.sessions_count} sessions · {report.records_count} présences enregistrées.</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-0 hard-border">
                {[["Présents", report.totals.present, "text-[#10b981]"], ["Retards", report.totals.present_retard, "text-[#f59e0b]"], ["Abs. motivées", report.totals.absent_motive, "text-[#3b82f6]"], ["Absents", report.totals.absent_non_motive, "text-[#D90429]"], ["Essais", report.totals.essai, "text-cyan-600"]].map(([l, v, c], idx) => (
                  <div key={l} className={`px-4 py-4 ${idx < 4 ? "hard-border-r" : ""}`}>
                    <div className="ticker text-muted-foreground text-xs">{l}</div>
                    <div className={`text-2xl font-black ${c}`}>{v}</div>
                  </div>
                ))}
              </div>
              <div>
                <h3 className="font-display font-bold uppercase mb-2">Par discipline</h3>
                <div className="space-y-1">
                  {report.by_discipline.map((d) => (
                    <div key={d.discipline} className="hard-border px-3 py-2 flex justify-between text-sm" data-testid={`report-disc-${d.discipline}`}>
                      <span className="capitalize">{(d.discipline || "").replace(/_/g, " ")}</span>
                      <span className="text-muted-foreground">{d.present} présents · {d.retard} retards · {d.total} total</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-display font-bold uppercase mb-2">Présences par membre (top)</h3>
                <div className="space-y-1">
                  {report.by_member.slice(0, 30).map((m) => (
                    <div key={m.contract_id} className="hard-border px-3 py-2 flex justify-between text-sm" data-testid={`report-member-${m.contract_id}`}>
                      <span>{m.full_name}</span>
                      <span className="text-muted-foreground">{m.present + m.retard} présences {m.retard ? `· ${m.retard} retards` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
